import { populateMapFromUserAppStartup } from 'App/Map/_utils/populateMapFromUserAppStartup'
import { updateMapWithAnyNeededCurrentValues } from 'App/Map/CurrentValuesMap/_utils/updateMapWithAnyNeededCurrentValues'
import { SelectedMapCard } from 'App/Map/CurrentValuesMap/components/SelectedMapCard/SelectedMapCard'
import { MapBase } from 'App/Map/MapContext/MapBase'
import { hubConnectorSettings } from 'settings/hubConnectorSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { smallStore } from 'stores/smallStore/smallStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { makeCoalescingRunner } from 'utils/makeCoalescingRunner'
import { BlockScdsValuesCache } from './caches/BlockScdsValuesCache/BlockScdsValuesCache'
import { BlocksDefaultPolygonsCache } from './caches/BlocksDefaultPolygonsCache/BlocksDefaultPolygonsCache'
import { BlockValuesCache } from './caches/BlockValuesCache/BlockValuesCache'
import { ImageOverlaysCache } from './caches/ImageOverlaysCache/ImageOverlaysCache'
import { IrrigationFlowMeterValuesCache } from './caches/IrrigationFlowMeterValuesCache/IrrigationFlowMeterValuesCache'
import { IrrigationZonesPolygonsCache } from './caches/IrrigationZonesPolygonsCache/IrrigationZonesPolygonsCache'
import { IrrigationZoneValuesCache } from './caches/IrrigationZoneValuesCache/IrrigationZoneValuesCache'
import { PropertyValuesCache } from './caches/PropertyValuesCache/PropertyValuesCache'
import { RegionValuesCache } from './caches/RegionsValuesCache/RegionsValuesCache'
import { StationValuesCache } from './caches/StationValuesCache/StationValuesCache'
import { TrapValuesCache } from './caches/TrapValuesCache/TrapValuesCache'
import { WindMachineValuesCache } from './caches/WindMachineValuesCache/WindMachineValuesCache'

export const initCurrentValuesMap = (map: google.maps.Map, maps: typeof google.maps) => {
  const blocksDefaultPolygonsCache = new BlocksDefaultPolygonsCache({ maps, map })
  const blockScdsValuesCache = new BlockScdsValuesCache({ maps, map })
  const blockValuesCache = new BlockValuesCache({ maps, map })
  const propertyValuesCache = new PropertyValuesCache({ maps, map })
  const stationValuesCache = new StationValuesCache({ maps, map })
  const trapValuesCache = new TrapValuesCache({ maps, map })
  const regionValuesCache = new RegionValuesCache({ maps, map })
  const irrigationZonePolygonsCache = new IrrigationZonesPolygonsCache({ maps, map })
  const irrigationZoneValuesCache = new IrrigationZoneValuesCache({ maps, map })
  const irrigationFlowMeterValuesCache = new IrrigationFlowMeterValuesCache({ maps, map })
  const imageOverlaysCache = new ImageOverlaysCache({ maps, map })
  const windMachineValuesCache = new WindMachineValuesCache({ maps, map })

  const caches = [
    blocksDefaultPolygonsCache,
    blockScdsValuesCache,
    blockValuesCache,
    propertyValuesCache,
    stationValuesCache,
    trapValuesCache,
    regionValuesCache,
    irrigationZonePolygonsCache,
    irrigationZoneValuesCache,
    irrigationFlowMeterValuesCache,
    imageOverlaysCache,
    windMachineValuesCache,
  ]

  const { properties, regions } = fieldAssetStore.getState()

  // TODO: proper try catching... but really this is all going to be moved soon anyways.
  populateMapFromUserAppStartup({
    map,
    maps,
    blocksDefaultPolygonsCache,
    blockScdsValuesCache,
    blockValuesCache,
    properties,
    propertyValuesCache,
    stationValuesCache,
    trapValuesCache,
    imageOverlaysCache,
    irrigationZonePolygonsCache,
    irrigationZoneValuesCache,
    irrigationFlowMeterValuesCache,
    regions,
    regionValuesCache,
    windMachineValuesCache,
  })

  const refreshRunner = makeCoalescingRunner({
    run: (isStopped) =>
      updateMapWithAnyNeededCurrentValues({
        blocksDefaultPolygonsCache,
        blockScdsValuesCache,
        blockValuesCache,
        propertyValuesCache,
        stationValuesCache,
        trapValuesCache,
        imageOverlaysCache,
        irrigationZonePolygonsCache,
        irrigationZoneValuesCache,
        irrigationFlowMeterValuesCache,
        regionValuesCache,
        windMachineValuesCache,
        isStale: isStopped,
      }),
    onStart: () => mapControlsStore.setState((s) => ({ ...s, loadingCurrentValues: true })),
    onEnd: () => mapControlsStore.setState((s) => ({ ...s, loadingCurrentValues: false })),
  })

  const refreshMap = refreshRunner.request

  const mapIdleEventListener = map.addListener('idle', () => {
    refreshMap()
  })

  const selectedValueGroupsListener = selectedValueGroupsStore.subscribeToChanges(
    (s) => [s.primaryValueGroup, s.selectedValueGroups],
    () => {
      refreshMap()
    },
  )

  const smallListener = smallStore.subscribeToChanges(
    (s) => [
      s.appliedIrrigationActivityDisplay,
      s.showDataSource,
      s.showRegionalData,
      s.showSprayRecordsModal,
    ],
    () => {
      refreshMap()
    },
  )

  const mapControlsListener = mapControlsStore.subscribeToChanges(
    ({ heatmapColoring, heatmapExtremes, loadingCurrentValues, showMapBadgeNames, ...rest }) => rest,
    () => {
      refreshMap()
    },
  )

  const userDetailsStoreListener = userDetailsStore.subscribeToChanges(
    (s) => [s.depth, s.rain, s.speed, s.temperature, s.volume, s.flowRate, s.pressure],
    () => {
      refreshMap()
    },
  )

  const selectedBlockListener = hubConnectorSettings.enableBlockPolygons
    ? selectedFieldAssetsStore.subscribeToChanges(
        (s) => s.block,
        (selectedBlock, oldSelectedBlock) => {
          const selectedBlockPolygonDefault = selectedBlock
            ? blocksDefaultPolygonsCache.items[selectedBlock]?.overlay
            : undefined

          const unSelectedBlockPolygonDefault = oldSelectedBlock
            ? blocksDefaultPolygonsCache.items[oldSelectedBlock]?.overlay
            : undefined

          selectedBlockPolygonDefault?.set('strokeColor', 'yellow')

          selectedBlockPolygonDefault?.set('strokeWeight', 4)

          unSelectedBlockPolygonDefault?.set('strokeColor', 'white')

          unSelectedBlockPolygonDefault?.set('strokeWeight', 2)

          const selectedBlockPolygonValue = selectedBlock
            ? blockValuesCache.items[selectedBlock]?.overlay?.polygon
            : undefined

          const unSelectedBlockPolygonValue = oldSelectedBlock
            ? blockValuesCache.items[oldSelectedBlock]?.overlay?.polygon
            : undefined

          selectedBlockPolygonValue?.set('strokeColor', 'yellow')

          selectedBlockPolygonValue?.set('strokeWeight', 4)

          unSelectedBlockPolygonValue?.set('strokeColor', 'white')

          unSelectedBlockPolygonValue?.set('strokeWeight', 2)
        },
      )
    : undefined

  const selectedIrrigationZoneListener = hubConnectorSettings.enableIrrigationZonePolygons
    ? selectedFieldAssetsStore.subscribeToChanges(
        (s) => s.irrigationZoneEmitter,
        (selectedIrrigationZone, oldSelectedIrrigationZone) => {
          const selectedIrrigationZonePolygon =
            irrigationZonePolygonsCache.items && selectedIrrigationZone
              ? irrigationZonePolygonsCache.items[selectedIrrigationZone]?.overlay
              : undefined

          const unSelectedIrrigationZonePolygon =
            irrigationZonePolygonsCache.items && oldSelectedIrrigationZone
              ? irrigationZonePolygonsCache.items[oldSelectedIrrigationZone]?.overlay
              : undefined

          selectedIrrigationZonePolygon?.set('strokeColor', 'yellow')

          selectedIrrigationZonePolygon?.set('strokeWeight', 4)

          unSelectedIrrigationZonePolygon?.set('strokeColor', 'white')

          unSelectedIrrigationZonePolygon?.set('strokeWeight', 2)

          const selectedIrrigationZonePolygonValue = selectedIrrigationZone
            ? irrigationZoneValuesCache.items[selectedIrrigationZone]?.overlay?.polygon
            : undefined

          const unSelectedIrrigationZonePolygonValue = oldSelectedIrrigationZone
            ? irrigationZoneValuesCache.items[oldSelectedIrrigationZone]?.overlay?.polygon
            : undefined

          selectedIrrigationZonePolygonValue?.set('strokeColor', 'yellow')

          selectedIrrigationZonePolygonValue?.set('strokeWeight', 4)

          unSelectedIrrigationZonePolygonValue?.set('strokeColor', 'white')

          unSelectedIrrigationZonePolygonValue?.set('strokeWeight', 2)
        },
      )
    : undefined

  return () => {
    refreshRunner.stop()

    mapIdleEventListener.remove()

    selectedBlockListener?.() // unsubscribes

    selectedValueGroupsListener() // unsubscribes

    smallListener() // unsubscribes

    userDetailsStoreListener() // unsubscribes

    selectedIrrigationZoneListener?.() // unsubscribes

    mapControlsListener() // unsubscribes

    caches.forEach((cache) => cache.dispose())
  }
}

export const CurrentValuesMap = (props: {
  hasAppStartupData: boolean // TODO: Move...
}) => {
  return (
    <MapBase hasAppStartupData={props.hasAppStartupData} loadingData={false} onInit={initCurrentValuesMap}>
      <SelectedMapCard />
    </MapBase>
  )
}
